import type {
    DroppedContent,
    ListContext,
    ParserContext,
    ParagraphResult,
} from './types';
import {
    escapeHtml,
    isBullet,
    isSafeUrl,
    isToggleOn,
    olTypeAttr,
} from './types';
import {
    findAll,
    flattenChildren,
    getAttr,
    getChild,
    getChildren,
} from './xmlUtils';

// ── Content Processors ───────────────────────────────────────────────────────

function processRun(run: Element, dropped: DroppedContent): string {
    const rPr = getChild(run, 'rPr');
    const texts: string[] = [];

    for (let i = 0; i < run.childNodes.length; i++) {
        const node = run.childNodes[i];
        if (!(node instanceof Element)) continue;
        switch (node.localName) {
            case 't':
                texts.push(escapeHtml(node.textContent || ''));
                break;
            case 'br':
                texts.push('<br>');
                break;
            case 'tab':
                texts.push('\t');
                break;
            case 'drawing':
                dropped.images++;
                break;
            case 'pict':
                if (findAll(node, 'rect').length === 0) dropped.images++;
                break;
            case 'object':
                dropped.objects++;
                break;
            case 'footnoteReference':
                dropped.footnotes++;
                break;
            case 'endnoteReference':
                dropped.endnotes++;
                break;
        }
    }

    let text = texts.join('');
    if (!text) return '';

    if (rPr) {
        if (isToggleOn(getChild(rPr, 'b'))) text = `<strong>${text}</strong>`;
        if (isToggleOn(getChild(rPr, 'i'))) text = `<em>${text}</em>`;
        if (getChild(rPr, 'u')) text = `<u>${text}</u>`;
        if (getChild(rPr, 'strike')) text = `<s>${text}</s>`;
        if (getChild(rPr, 'highlight')) text = `<mark>${text}</mark>`;
    }

    return text;
}

function processHyperlink(el: Element, ctx: ParserContext): string {
    const rId = getAttr(el, 'id');
    const url = ctx.rels.get(rId) || '';
    const linkText = getChildren(el, 'r')
        .map((r) => processRun(r, ctx.dropped))
        .join('');

    if (url && isSafeUrl(url)) {
        return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    }
    return linkText;
}

function processFieldCodes(
    elements: Element[],
    ctx: ParserContext
): string {
    let content = '';
    let fieldUrl: string | null = null;
    let fieldTextParts: string[] = [];
    let inField = false;

    for (const el of elements) {
        if (el.localName === 'hyperlink') {
            content += processHyperlink(el, ctx);
            continue;
        }

        if (el.localName !== 'r') continue;

        const fldChar = getChild(el, 'fldChar');
        if (fldChar) {
            const fldType = getAttr(fldChar, 'fldCharType');
            if (fldType === 'begin') {
                inField = true;
                fieldUrl = null;
                fieldTextParts = [];
            } else if (fldType === 'end') {
                if (
                    fieldUrl &&
                    isSafeUrl(fieldUrl) &&
                    fieldTextParts.length > 0
                ) {
                    content += `<a href="${escapeHtml(fieldUrl)}" target="_blank" rel="noopener noreferrer">${fieldTextParts.join('')}</a>`;
                } else if (fieldTextParts.length > 0) {
                    content += fieldTextParts.join('');
                }
                inField = false;
                fieldUrl = null;
                fieldTextParts = [];
            }
            continue;
        }

        const instrText = getChild(el, 'instrText');
        if (instrText) {
            const instrContent = (instrText.textContent || '').trim();
            const hyperlinkMatch = instrContent.match(
                /HYPERLINK\s+"([^"]+)"/
            );
            if (hyperlinkMatch) {
                fieldUrl = hyperlinkMatch[1];
            }
            continue;
        }

        if (inField) {
            fieldTextParts.push(processRun(el, ctx.dropped));
        } else {
            content += processRun(el, ctx.dropped);
        }
    }

    return content;
}

// ── Paragraph Processing ─────────────────────────────────────────────────────

function readParagraphProperties(
    pPr: Element | null,
    ctx: ParserContext
): { listContext: ListContext | null; headingLevel: number; alignAttr: string } {
    if (!pPr)
        return { listContext: null, headingLevel: 0, alignAttr: '' };

    let listContext: ListContext | null = null;
    const numPr = getChild(pPr, 'numPr');
    if (numPr) {
        const ilvlEl = getChild(numPr, 'ilvl');
        const numIdEl = getChild(numPr, 'numId');
        const level = parseInt(ilvlEl ? getAttr(ilvlEl, 'val') || '0' : '0');
        const nId = numIdEl ? getAttr(numIdEl, 'val') || '' : '';
        if (nId && nId !== '0') {
            const format =
                ctx.numbering.get(nId)?.get(level) ?? 'decimal';
            listContext = { numId: nId, level, format };
        }
    }

    let headingLevel = 0;
    const pStyle = getChild(pPr, 'pStyle');
    if (pStyle) {
        const styleId = getAttr(pStyle, 'val');
        headingLevel = ctx.headingStyles.get(styleId) ?? 0;
    }

    let alignAttr = '';
    const jc = getChild(pPr, 'jc');
    if (jc) {
        const val = getAttr(jc, 'val');
        if (val === 'center' || val === 'right')
            alignAttr = ` style="text-align: ${val}"`;
        else if (val === 'both')
            alignAttr = ' style="text-align: justify"';
    }

    return { listContext, headingLevel, alignAttr };
}

function detectHorizontalRule(
    pPr: Element | null,
    runs: Element[]
): boolean {
    const hasText = runs.some((r) => {
        const t = getChild(r, 't');
        return t && (t.textContent || '').trim().length > 0;
    });
    if (hasText) return false;

    if (pPr && getChild(pPr, 'pBdr')) return true;

    return runs.some((r) => {
        const pict = getChild(r, 'pict');
        return pict !== null && findAll(pict, 'rect').length > 0;
    });
}

function processParagraph(
    para: Element,
    ctx: ParserContext
): ParagraphResult {
    const pPr = getChild(para, 'pPr');
    const flatChildren = flattenChildren(para);
    const runs = flatChildren.filter((e) => e.localName === 'r');

    if (detectHorizontalRule(pPr, runs)) {
        return { html: '<hr>', listContext: null, isHr: true };
    }

    const { listContext, headingLevel, alignAttr } =
        readParagraphProperties(pPr, ctx);

    const content = processFieldCodes(flatChildren, ctx);

    if (!content.trim()) {
        return {
            html: `<p${alignAttr}><br></p>`,
            listContext,
            isHr: false,
        };
    }

    if (headingLevel >= 1 && headingLevel <= 6) {
        return {
            html: `<h${headingLevel}${alignAttr}>${content}</h${headingLevel}>`,
            listContext: null,
            isHr: false,
        };
    }

    if (listContext) {
        return { html: content, listContext, isHr: false };
    }

    return {
        html: `<p${alignAttr}>${content}</p>`,
        listContext: null,
        isHr: false,
    };
}

// ── Block Processing ─────────────────────────────────────────────────────────

function isFollowedByBlock(parent: Element, startIndex: number): boolean {
    for (let j = startIndex + 1; j < parent.childNodes.length; j++) {
        const next = parent.childNodes[j];
        if (!(next instanceof Element)) continue;
        if (next.localName === 'tbl') return true;
        if (next.localName === 'p') {
            const nextPPr = getChild(next, 'pPr');
            if (nextPPr) {
                const nextStyle = getChild(nextPPr, 'pStyle');
                if (
                    nextStyle &&
                    (getAttr(nextStyle, 'val') || '').match(/^[Hh]eading/)
                ) {
                    return true;
                }
                if (getChild(nextPPr, 'pBdr')) return true;
            }
            const nextRuns = flattenChildren(next).filter(
                (e) => e.localName === 'r'
            );
            return nextRuns.some((r) => {
                const pict = getChild(r, 'pict');
                return pict !== null && findAll(pict, 'rect').length > 0;
            });
        }
        return false;
    }
    return false;
}

export function processBlockChildren(
    parent: Element,
    ctx: ParserContext
): string {
    let html = '';
    let lastWasEmpty = false;
    const openLists: { format: string; numId: string }[] = [];

    const openList = (
        format: string,
        numId: string,
        content: string
    ) => {
        const tag = isBullet(format)
            ? '<ul>'
            : `<ol${olTypeAttr(format)}>`;
        html += `${tag}<li>${content}`;
        openLists.push({ format, numId });
    };

    const closeListsTo = (depth: number) => {
        while (openLists.length > depth) {
            const list = openLists.pop()!;
            html += `</li>${isBullet(list.format) ? '</ul>' : '</ol>'}`;
        }
    };

    for (let i = 0; i < parent.childNodes.length; i++) {
        const node = parent.childNodes[i];
        if (!(node instanceof Element)) continue;

        if (node.localName === 'tbl') {
            closeListsTo(0);
            lastWasEmpty = false;
            html += processTable(node, ctx);
            continue;
        }

        if (node.localName !== 'p') continue;

        const result = processParagraph(node, ctx);
        const isEmpty = result.html.startsWith('<p') && result.html.endsWith('<br></p>');

        if (isEmpty) {
            if (lastWasEmpty) continue;
            lastWasEmpty = true;
            if (html.endsWith('</table>') || html.endsWith('<hr>'))
                continue;
            if (isFollowedByBlock(parent, i)) continue;
        } else {
            lastWasEmpty = false;
        }

        if (result.isHr) {
            closeListsTo(0);
            lastWasEmpty = true;
            html += result.html;
            continue;
        }

        if (!result.listContext) {
            closeListsTo(0);
            html += result.html;
            continue;
        }

        const { listContext } = result;
        const targetDepth = listContext.level + 1;

        if (openLists.length >= targetDepth) {
            const current = openLists[targetDepth - 1];
            if (current.numId !== listContext.numId) {
                closeListsTo(targetDepth - 1);
            }
        }

        if (targetDepth > openLists.length) {
            while (openLists.length < targetDepth) {
                if (openLists.length === targetDepth - 1) {
                    openList(
                        listContext.format,
                        listContext.numId,
                        result.html
                    );
                } else {
                    openList(listContext.format, listContext.numId, '');
                }
            }
        } else if (targetDepth < openLists.length) {
            closeListsTo(targetDepth);
            html += `</li><li>${result.html}`;
        } else {
            html += `</li><li>${result.html}`;
        }
    }

    closeListsTo(0);
    return html;
}

export function processTable(
    table: Element,
    ctx: ParserContext
): string {
    let html = '<table>';
    for (const row of getChildren(table, 'tr')) {
        const trPr = getChild(row, 'trPr');
        const isHeaderRow =
            trPr !== null && getChild(trPr, 'tblHeader') !== null;
        const cellTag = isHeaderRow ? 'th' : 'td';

        html += '<tr>';
        for (const cell of getChildren(row, 'tc')) {
            const tcPr = getChild(cell, 'tcPr');

            let colspanAttr = '';
            if (tcPr) {
                const gridSpan = getChild(tcPr, 'gridSpan');
                const span = gridSpan
                    ? parseInt(getAttr(gridSpan, 'val') || '1')
                    : 1;
                if (span > 1) colspanAttr = ` colspan="${span}"`;
            }

            html += `<${cellTag}${colspanAttr}>`;
            html += processBlockChildren(cell, ctx);
            html += `</${cellTag}>`;
        }
        html += '</tr>';
    }
    html += '</table>';
    return html;
}
